<template>
  <div class="ai-assistant" :class="theme">
    <!-- 悬浮球 -->
    <div v-show="!panelVisible" class="ai-fab" title="功率预测-智能体" @click="panelVisible = true">
      <span class="ai-fab-ring"></span>
      <i class="el-icon-cpu"></i>
    </div>

    <!-- 聊天面板 -->
    <transition name="ai-panel">
      <div v-show="panelVisible" class="ai-panel">
        <div class="ai-panel-header">
          <div class="ai-header-left">
            <span class="ai-header-avatar"><i class="el-icon-cpu"></i></span>
            <div class="ai-header-text">
              <span class="ai-title">功率预测-智能体</span>
              <span class="ai-subtitle">
                <i v-if="streaming" class="ai-dot"></i>
                {{ streaming ? '正在回答…' : '在线' }}
              </span>
            </div>
          </div>
          <span class="ai-header-actions">
            <i class="el-icon-delete" title="清空对话" @click="clearChat"></i>
            <i class="el-icon-close" title="收起" @click="panelVisible = false"></i>
          </span>
        </div>

        <!-- 场景分类 -->
        <div class="ai-scene-bar">
          <button
            v-for="s in scenes"
            :key="s.key"
            class="ai-scene-tab"
            :class="{ active: activeScene === s.key }"
            @click="activeScene = s.key"
          >
            <i :class="s.icon"></i>{{ s.label }}
          </button>
        </div>

        <div ref="msgList" class="ai-messages">
          <div v-if="!messages.length" class="ai-empty">
            <div class="ai-empty-icon"><i class="el-icon-cpu"></i></div>
            <p class="ai-empty-title">你好，我是功率预测智能体</p>
            <p class="ai-empty-sub">可以询问场站、风机、功率预测等相关问题</p>
            <div class="ai-suggestions">
              <span
                v-for="q in currentPrompts"
                :key="q"
                class="ai-suggestion"
                @click="askSuggestion(q)"
              >{{ q }}</span>
            </div>
          </div>
          <template v-for="(m, i) in messages">
            <!-- 工具调用卡片 -->
            <div v-if="m.tool" :key="'t' + i" class="ai-tool-card" :class="m.toolStatus">
              <i :class="m.toolStatus === 'running' ? 'el-icon-loading' : 'el-icon-circle-check'"></i>
              <span>{{ m.toolStatus === 'running' ? '正在调用工具' : '工具调用完成' }}：{{ m.tool }}</span>
            </div>
            <!-- 普通消息气泡 -->
            <div v-else :key="'m' + i" class="ai-msg" :class="m.role">
              <span v-if="m.role === 'assistant'" class="ai-msg-avatar"><i class="el-icon-cpu"></i></span>
              <div class="ai-bubble-wrap">
                <div class="ai-bubble">
                  <div v-if="m.role === 'assistant'" class="ai-md" v-html="renderContent(m.content)"></div>
                  <template v-else>{{ m.content }}</template>
                  <AiChart v-if="m.role === 'assistant' && m.chart" :chart="m.chart" :theme="theme" />
                  <span v-if="m.streaming" class="ai-cursor">▍</span>
                </div>
                <!-- 复制按钮：assistant 气泡 hover 显示 -->
                <span
                  v-if="m.role === 'assistant'"
                  class="ai-copy-btn"
                  :title="copiedIndex === i ? '已复制' : '复制'"
                  @click="copyMessage(m, i)"
                >
                  <i :class="copiedIndex === i ? 'el-icon-check' : 'el-icon-document-copy'"></i>
                </span>
              </div>
              <span v-if="m.role === 'user'" class="ai-msg-avatar user"><i class="el-icon-user"></i></span>
            </div>
          </template>
        </div>

        <div v-if="messages.length" class="ai-suggestions ai-conversation-suggestions">
          <button
            v-for="q in currentPrompts"
            :key="q"
            type="button"
            class="ai-suggestion"
            :disabled="streaming"
            @click="askSuggestion(q)"
          >{{ q }}</button>
        </div>

        <div class="ai-input-area">
          <el-input
            v-model="inputText"
            type="textarea"
            :autosize="{ minRows: 1, maxRows: 3 }"
            placeholder="请输入问题，Enter 发送，Shift+Enter 换行"
            :disabled="streaming"
            @keydown.native="handleKeydown"
          />
          <button
            v-if="!streaming"
            class="ai-send-btn"
            :disabled="!inputText.trim()"
            title="发送"
            @click="send"
          ><i class="el-icon-position"></i></button>
          <button v-else class="ai-send-btn stop" title="停止" @click="stop">
            <i class="el-icon-video-pause"></i>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { chatStream } from '@/api/ai'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import { focusMap, drawBoundary } from '@/store/agentMapStore'
import AiChart from './AiChart.vue'

// GFM：支持表格；单换行转 <br>
marked.use({ gfm: true, breaks: true })

export default {
  name: 'AiAssistant',
  components: {
    AiChart
  },
  props: {
    // 主题：dark（大屏）/ light（后台）
    theme: { type: String, default: 'light' }
  },
  data() {
    return {
      panelVisible: false,
      messages: [],
      inputText: '',
      streaming: false,
      abortFn: null,
      currentAiMsg: null,
      copiedIndex: -1, // 当前已复制的消息下标，用于按钮状态反馈
      // 场景分类：切换时联动下方提示词
      activeScene: 'station',
      scenes: [
        {
          key: 'station',
          label: '场站查询',
          icon: 'el-icon-office-building',
          prompts: [
            '查询我的场站列表',
            '风电场1号风机的机组型号是什么？',
            '光伏电站今天的辐射情况如何？',
            '我的场站分布在哪几个区域？'
          ]
        },
        {
          key: 'stat',
          label: '发电统计',
          icon: 'el-icon-data-analysis',
          prompts: [
            '今天整个场站发了多少度电？',
            '风电一场今天的发电趋势如何？',
            '昨天和今天的发电量对比',
            '本月累计发电量是多少？'
          ]
        },
        {
          key: 'forecast',
          label: '功率预测',
          icon: 'el-icon-s-data',
          prompts: [
            '查询未来三天的功率预测',
            '未来24小时的风速预测如何？',
            '未来十天发电量预测',
            '明天最大出力时段是什么时候？'
          ]
        },
        {
          key: 'alert',
          label: '预警监控',
          icon: 'el-icon-warning-outline',
          prompts: [
            '未来几天有哪些预警信息？',
            '有哪些设备处于故障状态？',
            '风电二场目前运行状态异常吗？',
            '最近一次极端天气预警是什么？'
          ]
        },
        {
          key: 'user',
          label: '用户管理',
          icon: 'el-icon-user',
          prompts: [
            '我的下属用户都有哪些？',
            '如何给下属用户授权场站？',
            '如何新增一个普通用户？',
            '怎么禁用或启用下属账号？'
          ]
        }
      ]
    }
  },
  computed: {
    // 当前场景对应的提示词
    currentPrompts() {
      const s = this.scenes.find((x) => x.key === this.activeScene)
      return s ? s.prompts : []
    }
  },
  beforeDestroy() {
    if (this.abortFn) this.abortFn()
  },
  methods: {
    // marked 解析 + DOMPurify 清洗，防 LLM 输出恶意 HTML
    renderContent(text) {
      return DOMPurify.sanitize(marked.parse(text || ''))
    },
    // 从工具输出提取地名（兼容全角括号）
    extractName(output) {
      const m = /已定位：(.+?)\s*[（(≈]/.exec(output || '')
      return m ? m[1].trim() : ''
    },
    askSuggestion(q) {
      this.inputText = q
      this.send()
    },
    // 复制 AI 回答到剪贴板
    copyMessage(m, i) {
      const text = (m.content || '').trim()
      if (!text) return
      const done = () => {
        this.copiedIndex = i
        setTimeout(() => {
          if (this.copiedIndex === i) this.copiedIndex = -1
        }, 2000)
      }
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(done).catch(() => this.fallbackCopy(text, done))
      } else {
        this.fallbackCopy(text, done)
      }
    },
    // 兼容非 HTTPS 环境：隐藏 textarea + execCommand 复制
    fallbackCopy(text, done) {
      const ta = document.createElement('textarea')
      ta.value = text
      ta.style.position = 'fixed'
      ta.style.opacity = '0'
      document.body.appendChild(ta)
      ta.select()
      try {
        document.execCommand('copy')
      } catch (e) {
        /* 忽略复制失败 */
      }
      document.body.removeChild(ta)
      done()
    },
    handleKeydown(e) {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        this.send()
      }
    },
    send() {
      const text = this.inputText.trim()
      if (!text || this.streaming) return
      this.messages.push({ role: 'user', content: text })
      this.inputText = ''
      this.streaming = true
      const aiMsg = { role: 'assistant', content: '', streaming: true }
      this.currentAiMsg = aiMsg
      this.messages.push(aiMsg)
      this.scrollToBottom()

      // 构建历史记录，排除正在流式生成的消息与工具卡片，限制为最近 16 条
      const history = this.messages
        .filter(m => !m.streaming && !m.tool)
        .map(m => ({ role: m.role, content: m.content }))
        .slice(-16)

      this.abortFn = chatStream(
        { message: text, history: history },
        {
          onEvent: (evt) => {
            if (evt.type === 'token') {
              aiMsg.content += evt.content || ''
              this.scrollToBottom()
            } else if (evt.type === 'tool_start') {
              // 在主气泡上方插入工具卡片（running 状态）
              const idx = this.messages.indexOf(aiMsg)
              this.messages.splice(idx, 0, {
                role: 'assistant', tool: evt.name || 'tool', toolStatus: 'running', content: ''
              })
              this.scrollToBottom()
            } else if (evt.type === 'tool_end') {
              // 把最近一条 running 卡片标记为完成，并触发地图联动
              for (let k = this.messages.length - 1; k >= 0; k--) {
                if (this.messages[k].toolStatus === 'running') {
                  this.messages[k].toolStatus = 'done'
                  break
                }
              }
              const name = this.extractName(evt.output)
              // 触发地图联动：点定位 + 边界勾画
              if (evt.coord && typeof evt.coord.lng === 'number' && typeof evt.coord.lat === 'number') {
                focusMap(evt.coord.lng, evt.coord.lat, name)
              }
              if (evt.boundary) drawBoundary(evt.boundary, name)
              // 工具返回时序数据 → 当前气泡内渲染图表
              if (evt.chart) {
                this.$set(aiMsg, 'chart', evt.chart)
              }
              this.scrollToBottom()
            } else if (evt.type === 'error') {
              aiMsg.content += (aiMsg.content ? '\n' : '') + '错误：' + (evt.message || '未知错误')
            }
          },
          onDone: () => {
            this.finishStream(aiMsg)
            if (!aiMsg.content) aiMsg.content = '（无回复内容）'
          },
          onError: (err) => {
            this.finishStream(aiMsg)
            if (!aiMsg.content) aiMsg.content = '请求失败：' + ((err && err.message) || '网络错误')
          }
        }
      )
    },
    stop() {
      if (this.abortFn) this.abortFn()
      if (this.currentAiMsg) {
        this.finishStream(this.currentAiMsg)
        if (!this.currentAiMsg.content) this.currentAiMsg.content = '（已停止）'
      } else {
        this.streaming = false
      }
    },
    finishStream(aiMsg) {
      aiMsg.streaming = false
      this.streaming = false
      this.abortFn = null
      this.currentAiMsg = null
      this.scrollToBottom()
    },
    clearChat() {
      if (this.abortFn) this.abortFn()
      this.streaming = false
      this.currentAiMsg = null
      this.messages = []
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.msgList
        if (el) el.scrollTop = el.scrollHeight
      })
    }
  }
}
</script>

<style scoped>
/* ============ 悬浮球 ============ */
.ai-fab {
  position: fixed;
  right: 24px;
  bottom: 32px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 3000;
  font-size: 26px;
  color: #fff;
  transition: transform 0.2s ease;
}
.ai-fab:hover { transform: scale(1.1); }
.ai-fab-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  animation: ai-pulse 2s ease-out infinite;
}
.light .ai-fab {
  background: linear-gradient(135deg, #4da3ff, #1d6fd8);
  box-shadow: 0 6px 20px rgba(45, 125, 220, 0.45);
}
.light .ai-fab-ring { border: 2px solid rgba(64, 158, 255, 0.6); }
.dark .ai-fab {
  background: linear-gradient(135deg, #28627e, #1c4864);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
}
.dark .ai-fab-ring { border: 2px solid rgba(91, 166, 196, 0.28); }
@keyframes ai-pulse {
  0% { transform: scale(1); opacity: 0.7; }
  100% { transform: scale(1.6); opacity: 0; }
}

/* ============ 面板 ============ */
.ai-panel {
  position: fixed;
  right: 32px;
  bottom: 40px;
  width: 780px;
  height: 86vh;
  min-height: 620px;
  max-width: calc(100vw - 64px);
  max-height: calc(100vh - 96px);
  border-radius: 28px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 3000;
}
.light .ai-panel {
  background: #fff;
  box-shadow: 0 24px 80px rgba(30, 60, 120, 0.22), 0 4px 16px rgba(30, 60, 120, 0.08);
  border: 1px solid #e4ecf7;
}
.dark .ai-panel {
  background: #0b1729;
  border: 1px solid rgba(133, 170, 211, 0.3);
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(196, 224, 255, 0.04);
}
.ai-panel-enter-active, .ai-panel-leave-active { transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1); }
.ai-panel-enter, .ai-panel-leave-to { opacity: 0; transform: translateY(24px) scale(0.96); }

/* ============ 头部 ============ */
.ai-panel-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
}
.light .ai-panel-header {
  background: linear-gradient(135deg, #3a8eff, #2468d8);
  color: #fff;
}
.dark .ai-panel-header {
  background: linear-gradient(155deg, #14273e, #101f34);
  color: #dceaf8;
  border-bottom: 1px solid rgba(133, 170, 211, 0.18);
}
.ai-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}
.ai-header-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}
.light .ai-header-avatar { background: rgba(255, 255, 255, 0.22); }
.dark .ai-header-avatar { background: rgba(67, 175, 207, 0.14); }
.ai-header-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.ai-title {
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.5px;
  line-height: 1;
}
.ai-subtitle {
  font-size: 11px;
  opacity: 0.85;
  display: flex;
  align-items: center;
  gap: 6px;
  line-height: 1;
}
.ai-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #67c23a;
  animation: ai-blink 1s ease-in-out infinite;
}
.ai-header-actions i {
  margin-left: 18px;
  cursor: pointer;
  font-size: 18px;
  opacity: 0.75;
  transition: opacity 0.2s, transform 0.2s;
}
.ai-header-actions i:hover { opacity: 1; transform: scale(1.12); }

/* ============ 场景分类栏 ============ */
.ai-scene-bar {
  flex-shrink: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 32px;
}
.light .ai-scene-bar {
  background: #f5f8fc;
  border-bottom: 1px solid #e8ecf3;
}
.dark .ai-scene-bar {
  background: #101e31;
  border-bottom: 1px solid rgba(133, 170, 211, 0.16);
}
.ai-scene-tab {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  font-size: 14px;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid transparent;
}
.light .ai-scene-tab {
  color: #5a6b85;
  background: #fff;
  border-color: #d9e2f0;
}
.light .ai-scene-tab:hover {
  color: #2468d8;
  border-color: #2468d8;
}
.light .ai-scene-tab.active {
  color: #fff;
  background: linear-gradient(135deg, #3a8eff, #2468d8);
  border-color: #2468d8;
  box-shadow: 0 4px 12px rgba(36, 104, 216, 0.3);
}
.dark .ai-scene-tab {
  color: #a6c5df;
  background: rgba(23, 44, 66, 0.5);
  border-color: rgba(130, 170, 207, 0.26);
}
.dark .ai-scene-tab:hover {
  color: #e2f4ff;
  border-color: #4c8cab;
}
.dark .ai-scene-tab.active {
  color: #e2f4ff;
  background: linear-gradient(135deg, #235b79, #1a415e);
  border-color: #4e9cbb;
  box-shadow: none;
}

/* ============ 消息区 ============ */
.ai-messages {
  flex: 1;
  overflow-y: auto;
  padding: 32px 36px;
}
.light .ai-messages { background: #f5f8fc; }
.dark .ai-messages { background: #0b1729; }

/* 空状态 */
.ai-empty {
  text-align: center;
  padding-top: 48px;
}
.ai-empty-icon {
  width: 96px;
  height: 96px;
  margin: 0 auto 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 46px;
}
.light .ai-empty-icon {
  background: linear-gradient(135deg, #e3f0ff, #cfe4ff);
  color: #2468d8;
}
.dark .ai-empty-icon {
  background: rgba(67, 175, 207, 0.08);
  color: #7bd6ee;
  box-shadow: none;
}
.ai-empty-title {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 8px;
}
.light .ai-empty-title { color: #303133; }
.dark .ai-empty-title { color: #eaf9ff; }
.ai-empty-sub {
  font-size: 14px;
  margin-bottom: 24px;
}
.light .ai-empty-sub { color: #909399; }
.dark .ai-empty-sub { color: rgba(234, 249, 255, 0.55); }
.ai-suggestions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding: 0 28px;
}
.ai-suggestion {
  padding: 10px 14px;
  border-radius: 12px;
  font-size: 13px;
  line-height: 1.4;
  cursor: pointer;
  transition: all 0.2s;
}
.ai-conversation-suggestions {
  flex-shrink: 0;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  padding: 12px 32px;
  max-height: 140px;
  overflow-y: auto;
}
.light .ai-conversation-suggestions { background: #f5f8fc; }
.dark .ai-conversation-suggestions { background: #0b1729; }
.ai-conversation-suggestions .ai-suggestion {
  font-family: inherit;
  text-align: left;
  overflow-wrap: anywhere;
}
.ai-conversation-suggestions .ai-suggestion:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}
.light .ai-suggestion {
  border: 1px solid #c9e0ff;
  background: #fff;
  color: #2468d8;
}
.light .ai-suggestion:hover {
  background: #2468d8;
  color: #fff;
  border-color: #2468d8;
}
.dark .ai-suggestion {
  border: 1px solid rgba(130, 170, 207, 0.24);
  background: #112339;
  color: #a6c5df;
}
.dark .ai-suggestion:hover {
  background: #1b3853;
  color: #e2f4ff;
}

/* 消息气泡 */
.ai-msg {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 24px;
}
.ai-msg.user { justify-content: flex-end; }
/* 气泡容器：相对定位，供复制按钮绝对定位 */
.ai-bubble-wrap {
  position: relative;
  max-width: 80%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}
.ai-msg.user .ai-bubble-wrap { align-items: flex-start; }
.ai-msg-avatar {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: #fff;
  margin-top: 2px;
}
.light .ai-msg-avatar { background: linear-gradient(135deg, #4da3ff, #1d6fd8); }
.light .ai-msg-avatar.user { background: linear-gradient(135deg, #a8b6c8, #7d8ea3); }
.dark .ai-msg-avatar { background: linear-gradient(135deg, #28617d, #1b405a); }
.dark .ai-msg-avatar.user { background: linear-gradient(135deg, #5a718c, #3d4f66); }

.ai-bubble {
  max-width: 100%;
  padding: 16px 20px;
  font-size: 16px;
  line-height: 1.75;
  word-break: break-word;
  white-space: normal;
  overflow-x: auto;
}
.ai-msg.assistant .ai-bubble { border-radius: 4px 20px 20px 20px; }
.ai-msg.user .ai-bubble { border-radius: 20px 4px 20px 20px; color: #fff; }
.light .ai-msg.assistant .ai-bubble {
  background: #fff;
  border: 1px solid #e8ecf3;
  color: #303133;
  box-shadow: 0 4px 14px rgba(30, 60, 120, 0.08);
}
.light .ai-msg.user .ai-bubble { background: linear-gradient(135deg, #4da3ff, #2468d8); box-shadow: 0 4px 14px rgba(45, 125, 220, 0.25); }
.dark .ai-msg.assistant .ai-bubble {
  background: #13263c;
  border: 1px solid rgba(133, 170, 211, 0.2);
  color: #dceaf8;
}
.dark .ai-msg.user .ai-bubble { background: linear-gradient(135deg, #235674, #1b415e); color: #e2f4ff; box-shadow: none; }
.ai-cursor {
  display: inline-block;
  margin-left: 2px;
  animation: ai-blink 0.8s steps(1) infinite;
}
@keyframes ai-blink { 50% { opacity: 0; } }

/* 复制按钮：assistant 气泡 hover 显示 */
.ai-copy-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  margin-top: 6px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  opacity: 0;
  transition: opacity 0.2s, background 0.2s, color 0.2s;
}
.ai-msg.assistant:hover .ai-copy-btn { opacity: 0.7; }
.ai-msg.assistant:hover .ai-copy-btn:hover { opacity: 1; }
.light .ai-copy-btn { color: #909399; }
.light .ai-copy-btn:hover { background: #e8eef8; color: #2468d8; }
.dark .ai-copy-btn { color: #8fb3c8; }
.dark .ai-copy-btn:hover { background: rgba(0, 216, 255, 0.18); color: #00d8ff; }

/* Markdown 内容 */
.ai-md >>> p { margin: 0 0 10px; }
.ai-md >>> p:last-child { margin-bottom: 0; }
.ai-md >>> h4 { margin: 10px 0 6px; font-size: 17px; }
.ai-md >>> ul, .ai-md >>> ol { padding-left: 24px; margin: 8px 0; }
.ai-md >>> pre {
  padding: 14px 16px;
  border-radius: 12px;
  overflow-x: auto;
  margin: 10px 0;
}
.light .ai-md >>> pre { background: #f0f2f5; }
.dark .ai-md >>> pre { background: rgba(0, 0, 0, 0.45); color: #7ee7ff; }
.ai-md >>> code { font-family: Consolas, Monaco, monospace; font-size: 14px; }

/* Markdown 表格（v-html 注入内容需 >>> 穿透） */
.ai-md >>> table {
  border-collapse: collapse;
  width: 100%;
  margin: 8px 0;
  font-size: 14px;
}
.ai-md >>> th, .ai-md >>> td {
  padding: 6px 10px;
  text-align: left;
}
.light .ai-md >>> th, .light .ai-md >>> td { border: 1px solid #d5e2f5; }
.light .ai-md >>> th { background: #eef5ff; color: #2468d8; }
.dark .ai-md >>> th, .dark .ai-md >>> td { border: 1px solid rgba(0, 220, 255, 0.2); }
.dark .ai-md >>> th { background: rgba(0, 150, 200, 0.15); }

/* 工具调用卡片 */
.ai-tool-card {
  display: flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  margin: 0 0 14px 58px;
  padding: 8px 16px;
  border-radius: 10px;
  font-size: 13px;
}
.light .ai-tool-card { background: #eef5ff; color: #2468d8; border: 1px solid #c9e0ff; }
.dark .ai-tool-card { background: #13263c; color: #a6c5df; border: 1px solid rgba(133, 170, 211, 0.2); }
.ai-tool-card.done { opacity: 0.7; }

/* ============ 输入区 ============ */
.ai-input-area {
  flex-shrink: 0;
  display: flex;
  align-items: flex-end;
  gap: 16px;
  padding: 22px 28px;
}
.light .ai-input-area { background: #fff; border-top: 1px solid #ebeef5; }
.dark .ai-input-area { background: #101f34; border-top: 1px solid rgba(133, 170, 211, 0.18); }
.ai-input-area >>> .el-textarea__inner {
  border-radius: 16px;
  padding: 14px 18px;
  font-size: 16px;
  line-height: 1.6;
}
.light .ai-input-area >>> .el-textarea__inner {
  background: #f5f8fc;
  border-color: #dce6f5;
}
.light .ai-input-area >>> .el-textarea__inner:focus {
  background: #fff;
  border-color: #4da3ff;
}
.dark .ai-input-area >>> .el-textarea__inner {
  background: #0b1727;
  border-color: rgba(128, 166, 207, 0.28);
  color: #d5e5f5;
}
.dark .ai-input-area >>> .el-textarea__inner::placeholder { color: #8298af; }

.ai-send-btn {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  font-size: 22px;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s, opacity 0.2s;
  outline: none;
}
.ai-send-btn:hover:not(:disabled) { transform: scale(1.08); }
.ai-send-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.light .ai-send-btn { background: linear-gradient(135deg, #4da3ff, #1d6fd8); box-shadow: 0 6px 16px rgba(45, 125, 220, 0.4); }
.dark .ai-send-btn { background: linear-gradient(135deg, #28627e, #1c4864); box-shadow: none; }
.ai-send-btn.stop { background: linear-gradient(135deg, #ff7d6b, #e04a35); box-shadow: 0 6px 16px rgba(224, 74, 53, 0.4); }

/* 深色对话框与风电、光伏卡片统一圆角与低亮度控件。 */
.dark .ai-panel { border-radius: 12px; }
.dark .ai-scene-tab { border-radius: 6px; }
.dark .ai-suggestion { border-radius: 8px; }
.dark .ai-msg.assistant .ai-bubble { border-radius: 4px 12px 12px; }
.dark .ai-msg.user .ai-bubble { border-radius: 12px 4px 12px 12px; }
.dark .ai-input-area >>> .el-textarea__inner { border-radius: 8px; }
.dark .ai-input-area >>> .el-textarea__inner:focus { border-color: #4e9cbb; }
.dark .ai-send-btn { border-radius: 8px; }
.dark .ai-send-btn.stop { background: #954a48; box-shadow: none; }
.dark .ai-fab-ring { animation: none; }
</style>
